import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Toast。
 *
 * 存在的理由不是"好看"，是**失败必须有可见的落点**。
 * 真实故障：附件上传的 await 外面没有任何 catch，抛了就是一个未处理的 promise rejection，
 * 图片凭空消失，界面上连个失败气泡都没有。用户以为发出去了，而少了哪几张只有对方看得出。
 *
 * 所以这个 Toast 刻意做了两件"不那么 toast"的事：
 * - **错误类不自动消失**，必须用户自己关掉或点重试；
 * - 支持一个 action（重试/撤销），因为大多数失败是可以补救的。
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
const ToastContext = createContext(null);
export function useToast() {
    const api = useContext(ToastContext);
    if (!api)
        throw new Error('useToast() 必须在 <ToastProvider> 里面用');
    return api;
}
const DEFAULT_DURATION = {
    info: 3000,
    success: 2400,
    warn: 4500,
    // 0 = 不自动消失。错误不该在用户还没看清之前就溜走。
    error: 0,
};
let seq = 0;
export function ToastProvider({ children, max = 3 }) {
    const [items, setItems] = useState([]);
    const timers = useRef(new Map());
    const dismiss = useCallback((id) => {
        const t = timers.current.get(id);
        if (t)
            clearTimeout(t);
        timers.current.delete(id);
        setItems((list) => list.filter((i) => i.id !== id));
    }, []);
    const show = useCallback((message, options = {}) => {
        const kind = options.kind ?? 'info';
        seq += 1;
        const id = `t${seq}`;
        const duration = options.duration ?? DEFAULT_DURATION[kind];
        const item = { id, message, kind, duration, ...(options.action ? { action: options.action } : {}) };
        setItems((list) => {
            const next = [...list, item];
            // 超出上限时挤掉最老的**非错误**项：错误必须留着让用户看见
            while (next.length > max) {
                const victim = next.find((i) => i.kind !== 'error') ?? next[0];
                const t = timers.current.get(victim.id);
                if (t)
                    clearTimeout(t);
                timers.current.delete(victim.id);
                next.splice(next.indexOf(victim), 1);
            }
            return next;
        });
        if (duration > 0)
            timers.current.set(id, setTimeout(() => dismiss(id), duration));
        return id;
    }, [dismiss, max]);
    const api = useMemo(() => ({
        show,
        info: (m, o) => show(m, { ...o, kind: 'info' }),
        success: (m, o) => show(m, { ...o, kind: 'success' }),
        warn: (m, o) => show(m, { ...o, kind: 'warn' }),
        error: (m, o) => show(m, { ...o, kind: 'error' }),
        dismiss,
        clear: () => {
            for (const t of timers.current.values())
                clearTimeout(t);
            timers.current.clear();
            setItems([]);
        },
    }), [show, dismiss]);
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, _jsx("div", { className: "ps-toast-layer", role: "region", "aria-live": "polite", "aria-label": "\u901A\u77E5", children: items.map((item) => (_jsxs("div", { className: `ps-toast ps-toast-${item.kind}`, children: [_jsx("span", { className: "ps-toast-msg", children: item.message }), item.action && (_jsx("button", { type: "button", className: "ps-toast-action", onClick: () => {
                                item.action?.onClick();
                                dismiss(item.id);
                            }, children: item.action.label })), item.duration === 0 && (_jsx("button", { type: "button", className: "ps-toast-close", "aria-label": "\u5173\u95ED", onClick: () => dismiss(item.id), children: "\u2715" }))] }, item.id))) })] }));
}
//# sourceMappingURL=toast.js.map